import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CountrySelector = () => {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [showQrModal, setShowQrModal] = useState(false);
  const [enrollLink, setEnrollLink] = useState('');
  const [selectedLinkId, setSelectedLinkId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([
    { value: '', label: 'Select Your Language', disabled: true }
  ]);
  const [loadingCountries, setLoadingCountries] = useState(true);

  const countryFlags = {
    'romania': '🇷🇴',
    'românia': '🇷🇴',
    'india': '🇮🇳',
    'croatia': '🇭🇷',
    'usa': '🇺🇸',
    'united states': '🇺🇸',
    'mexico': '🇲🇽'
  };

  // Fetch countries from admin panel on component mount
  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      // Fetch active countries from the API
      const response = await axios.get(`${API}/countries/active`);
      const activeCountries = response.data;
      
      // Language mapping for each country - usa is the main English page
      const languageMap = {
        'romania': 'Română',
        'usa': 'English',
        'mexico': 'Español',
        'india': 'हिन्दी',
        'croatia': 'Hrvatski'
      };
      
      // Filter and format countries - only include countries with language mappings
      const formattedCountries = activeCountries
        .filter(country => languageMap[country.name.toLowerCase()])
        .map(country => {
          const countryName = country.name.toLowerCase();
          return {
            value: countryName,
            label: languageMap[countryName],
            flag: country.flag_emoji || '🌍'
          };
        })
        .reduce((unique, item) => {
          // Remove duplicates by language label
          if (!unique.find(u => u.label === item.label)) {
            unique.push(item);
          }
          return unique;
        }, []);
      
      // Set countries with default option
      setCountries([
        { value: '', label: 'Select Your Language', disabled: true },
        ...formattedCountries
      ]);
      
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error('Failed to load languages');
      // Fallback to default countries if fetch fails
      setCountries([
        { value: '', label: 'Select Your Language', disabled: true },
        { value: 'romania', label: 'Română', flag: '🇷🇴' },
        { value: 'usa', label: 'English', flag: '🇺🇸' },
        { value: 'mexico', label: 'Español', flag: '🇲🇽' },
        { value: 'india', label: 'हिन्दी', flag: '🇮🇳' },
        { value: 'croatia', label: 'Hrvatski', flag: '🇭🇷' }
      ]);
    } finally {
      setLoadingCountries(false);
    }
  };

  const aboutData = {
    title: "About Electrum Prosperity Group",
    subtitle: "Invest, Earn, and Grow With Electrum Prosperity Group",
    description: "Start your journey with EPG today. This approach and model allows you to invest in your future while earning additional income through simple referrals. Wether youre a first-time investor or a seasoned professional, EPG educates you on a path to financial security and Growth.",
    sections: [
      {
        title: "What is EPG?",
        content: "Electrum Prosperity Group (EPG) is an education-driven community designed to help individuals build long-term wealth and financial confidence. We focus on precious metals, smart diversification, and modern income opportunities to help protect and grow wealth in any economy. Through guidance, mentorship, and a supportive network, EPG empowers people to create time, location, and financial freedom on their own terms."
      },
      {
        title: "Why Precious Metals",
        content: "Precious metals have stood the test of time as a reliable store of value through economic changes, inflation, and market uncertainty. Gold and silver offer protection against currency devaluation while adding stability and diversification to a well-rounded financial strategy. By incorporating precious metals, you're choosing tangible assets that help preserve purchasing power and support long-term wealth building in an ever-changing economy."
      },
      {
        title: "How Does it Work?",
        content: "Through our community, EPG is the gateway to premium access of gold and silver, knowledge to make smart investments, and opportunities to grow your income—all designed to create financial peace of mind."
      },
      {
        title: "EPG Benefits",
        content: "At Electrum Prosperity Group, we empower individuals and families to build real, lasting wealth through education, community, and access to high-quality precious metals. We connect you with trusted products, exclusive member benefits, and a simple pathway to start stacking silver and gold with confidence. Whether you're beginning your financial journey or diversifying your portfolio, EPG provides step-by-step guidance, tools, and opportunities to earn while you grow. Our mission is to help you create time, location, and financial freedom — one smart decision at a time"
      },
      {
        title: "Safety and Compliance",
        content: "Electrum Prosperity Group (EPG) is a community of entrepreneurs with the sole intention of sharing a wealth and prosperity building strategy that has worked for many of us. EPG does not offer a service and therefore has no income claims nor guarantees. EPG may refer to investing with precious metal collectibles and bullion. Remember all investments involve risk. EPG has no advisors licensed in any investment capacity and is not offering advice. Please do your own research before making any investment decisions. EPG bears no responsibility nor liability for any losses nor gains in your investments or collections. EPG has no influence on the pricing of precious metals nor the marketplace."
      }
    ]
  };

  const handleCountryChange = (e) => {
    const country = e.target.value;
    setSelectedCountry(country);
    
    // Reset any previously fetched link when country changes
    setEnrollLink('');
    setQrCodeUrl('');
    setSelectedLinkId(null);
  };

  // Link fetching logic moved into handleShowQrCode for on-demand loading

  const handleShowQrCode = async () => {
    // Special handling for Hindi - link to form page instead
    if (selectedCountry === 'india') {
      const formPageUrl = `${window.location.origin}/hindi-form`;
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(formPageUrl)}`;
      setEnrollLink(formPageUrl);
      setQrCodeUrl(qrUrl);
      setShowQrModal(true);
      return;
    }
    
    // If link is already fetched, show modal directly
    if (enrollLink && qrCodeUrl && selectedLinkId) {
      setShowQrModal(true);
      
      // Track QR code usage - decrement uses_left
      try {
        await axios.post(`${API}/join-links/track-qr-use/${selectedLinkId}`);
        console.log('QR code usage tracked successfully');
      } catch (error) {
        console.error('Error tracking QR code usage:', error);
        // Don't show error to user - tracking is background operation
      }
      return;
    }
    
    // If link is not yet fetched, fetch it first
    if (!enrollLink && selectedCountry) {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/join-links/`);
        const links = response.data;
        
        // Normalize country name for matching (case-insensitive)
        const countryName = selectedCountry.charAt(0).toUpperCase() + selectedCountry.slice(1);
        
        // Filter links that match the country and have uses left
        const matchingLinks = links.filter(link => {
          const linkCountries = link.country === 'All' 
            ? ['Romania', 'India', 'Croatia', 'USA', 'Mexico'] 
            : link.country.split(',').map(c => c.trim());
          
          // Case-insensitive comparison - must match country AND have uses left > 0
          return linkCountries.some(c => c.toLowerCase() === countryName.toLowerCase()) 
            && link.uses_left > 0 
            && link.link_to_be_used === 'yes';
        });
        
        // Sort by priority, then prefer "All", then by uses_left
        let selectedLink = null;
        if (matchingLinks.length > 0) {
          const sorted = matchingLinks.sort((a, b) => {
            // First sort by priority (lower number = higher priority)
            if (a.priority !== b.priority) {
              return a.priority - b.priority;
            }
            
            // Second, prefer "All" country (universal links)
            const aIsAll = a.country === 'All';
            const bIsAll = b.country === 'All';
            if (aIsAll && !bIsAll) return -1;  // a is "All", prefer it
            if (!aIsAll && bIsAll) return 1;   // b is "All", prefer it
            
            // Third, prefer links with more uses left
            return b.uses_left - a.uses_left;
          });
          selectedLink = sorted[0];
        }
        
        if (selectedLink) {
          setEnrollLink(selectedLink.join_now_link);
          setSelectedLinkId(selectedLink.id);
          // Generate QR code URL
          const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(selectedLink.join_now_link)}`;
          setQrCodeUrl(qrUrl);
          
          // Show modal immediately after setting state
          setShowQrModal(true);
          
          // Track QR code usage
          try {
            await axios.post(`${API}/join-links/track-qr-use/${selectedLink.id}`);
            console.log('QR code usage tracked successfully');
          } catch (error) {
            console.error('Error tracking QR code usage:', error);
          }
        } else {
          // No link available for this country
          toast.error('No link available for this country');
        }
      } catch (error) {
        console.error('Error fetching link:', error);
        toast.error('Failed to load enrollment link');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleGoToSite = () => {
    if (selectedCountry) {
      // Map country to language-based URL
      const languageRoutes = {
        'romania': '/romania',
        'mexico': '/espanol',
        'usa': '/english',
        'croatia': '/hrvatski',
        'india': '/hindi'
      };
      
      const route = languageRoutes[selectedCountry] || `/${selectedCountry}`;
      navigate(route);
    }
  };

  return (
    <div className="country-selector-page">
      {/* Header with Admin Button */}
      <div className="country-header">
        <Button 
          variant="outline" 
          className="country-admin-btn" 
          onClick={() => navigate('/admin/login')}
        >
          Admin Panel
        </Button>
      </div>

      {/* Country Selector Section */}
      <div className="country-selector-container">
        <img src="/epg_logo.png" alt="Electrum Prosperity Group" className="country-logo" />
        
        <h1 className="country-headline">
          Electrum Prosperity Group
        </h1>
        
        <p className="country-subheading">
          Select your language to get started
        </p>
        
        <div className="country-dropdown-wrapper">
          <select 
            value={selectedCountry}
            onChange={handleCountryChange}
            className="country-dropdown"
            disabled={loadingCountries}
          >
            {loadingCountries ? (
              <option value="">Loading languages...</option>
            ) : (
              countries.map((country) => (
                <option 
                  key={country.value} 
                  value={country.value}
                  disabled={country.disabled}
                >
                  {country.label}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Action Buttons - Show after country selection */}
        {selectedCountry && !loading && (
          <div className="country-action-buttons">
            <Button 
              className="country-action-btn qr-btn"
              onClick={handleShowQrCode}
            >
              QR Code to Join
            </Button>
            <Button 
              className="country-action-btn site-btn"
              onClick={handleGoToSite}
            >
              Go to Site
            </Button>
          </div>
        )}

        {loading && selectedCountry && (
          <div className="country-loading">
            Loading enrollment link...
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {showQrModal && (
        <div className="qr-modal-overlay" onClick={() => setShowQrModal(false)}>
          <div className="qr-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="qr-modal-close" onClick={() => setShowQrModal(false)}>
              ✕
            </button>
            <h2 className="qr-modal-title">Scan to Enroll</h2>
            <div className="qr-code-container">
              <img src={qrCodeUrl} alt="QR Code" className="qr-code-image" />
            </div>
            <p className="qr-modal-subtitle">
              Scan this code with your phone to enroll
            </p>
            <div className="qr-modal-link">
              <a href={enrollLink} target="_blank" rel="noopener noreferrer">
                {enrollLink}
              </a>
            </div>
            <Button 
              className="qr-modal-btn"
              onClick={() => setShowQrModal(false)}
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {/* About EPG Section */}
      <section className="country-about-section">
        <div className="country-about-container">
          <div className="country-about-header">
            <h2 className="country-about-title">{aboutData.title}</h2>
            <p className="country-about-subtitle">{aboutData.subtitle}</p>
            <p className="country-about-description">{aboutData.description}</p>
          </div>
          
          <div className="country-about-content">
            {aboutData.sections.map((section, index) => {
              // Render Safety and Compliance as a smaller disclaimer
              if (section.title === "Safety and Compliance") {
                return (
                  <div key={index} className="country-disclaimer">
                    <p className="country-disclaimer-text">{section.content}</p>
                  </div>
                );
              }
              // Render other sections as cards
              return (
                <Card key={index} className="country-about-card">
                  <CardContent className="country-about-card-content">
                    <h3 className="country-about-card-title">{section.title}</h3>
                    <p className="country-about-card-text">{section.content}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CountrySelector;
